import { CLASS_SIGNS } from '../../utils/arena/classSigns.data';

export const HUMOURS_SIGN = {
  image: '/mastery/backdrops/four-humours.jpg', width: 1920, height: 2523,
  cross: [0.505, 0.4425],
  stars: [[0.505, 0.4425, 1], [0.505, 0.09, 0.9], [0.505, 0.92, 0.9],
    [-0.28, 0.4425, 0.85], [1.28, 0.4425, 0.85],
    [0.14, 0.4425, 0.8], [0.84, 0.4425, 0.8],
    [0.48, 0.13, 0.9], [0.82, 0.21, 0.8], [0.16, 0.77, 0.8],
    [0.83, 0.77, 0.8], [0.42, 0.91, 0.65], [0.60, 0.91, 0.65]],
  links: [[0, 1], [0, 2], [0, 5], [5, 3], [0, 6], [6, 4],
    [7, 5], [7, 8], [8, 6], [5, 9], [6, 10], [9, 11], [10, 12]],
};

// Convert the scanned paper to light only while preparing the renderer's
// texture. The source file remains unchanged; paper contributes no alpha.
export function engravingAlpha(r, g, b) {
  return Math.pow(Math.max(0, 1 - (r * 0.2126 + g * 0.7152 + b * 0.0722) / 255), 1.5);
}

function engravingInk(image) {
  const canvas = document.createElement('canvas');
  canvas.width = 768; canvas.height = Math.round(768 * image.height / image.width);
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return image;
  ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
  const pixels = ctx.getImageData(0, 0, canvas.width, canvas.height);
  for (let i = 0; i < pixels.data.length; i += 4) {
    const x = (i / 4 % canvas.width) / canvas.width;
    const y = Math.floor(i / 4 / canvas.width) / canvas.height;
    // Exclude the scanned right-hand frame completely, then feather inward.
    // Keeping the canvas dimensions preserves the cross and star alignment.
    const edge = Math.max(0, Math.min(1, Math.min(x, 0.975 - x, y, 1 - y) / 0.055));
    const alpha = engravingAlpha(pixels.data[i], pixels.data[i + 1], pixels.data[i + 2]);
    pixels.data[i] = 212; pixels.data[i + 1] = 225; pixels.data[i + 2] = 247;
    pixels.data[i + 3] = alpha * edge * 255;
  }
  ctx.putImageData(pixels, 0, 0);
  return canvas;
}

export const FIGURE_KEYS = Object.keys(CLASS_SIGNS);
export const FIELD_W = 256;
export const FIELD_H = 384;

// The same screen rectangle positions both the GPU density and canvas stars.
export function figureRect(width, height) {
  const h = Math.min(height * 0.86, width * 0.9);
  return { x: (width - h * 2 / 3) / 2, y: height * 0.045, width: h * 2 / 3, height: h };
}

export function easeFigureWeights(weights, target, dt, snap = false) {
  const k = snap ? 1 : 1 - Math.exp(-dt / 0.85);
  for (let i = 0; i < weights.length; i++) weights[i] += ((i === target ? 1 : 0) - weights[i]) * k;
}

// Separable box diffusion: linear cost, zero outside the texture. Repeated
// passes approximate Gaussian density at several scales, never a crisp trace.
export function blurDensity(input, width, height, radius) {
  const temp = new Float32Array(input.length);
  const out = new Float32Array(input.length);
  const size = radius * 2 + 1;
  for (let y = 0; y < height; y++) {
    let sum = 0;
    for (let x = -radius; x < width; x++) {
      if (x + radius < width) sum += input[y * width + x + radius];
      if (x - radius - 1 >= 0) sum -= input[y * width + x - radius - 1];
      if (x >= 0) temp[y * width + x] = sum / size;
    }
  }
  for (let x = 0; x < width; x++) {
    let sum = 0;
    for (let y = -radius; y < height; y++) {
      if (y + radius < height) sum += temp[(y + radius) * width + x];
      if (y - radius - 1 >= 0) sum -= temp[(y - radius - 1) * width + x];
      if (y >= 0) out[y * width + x] = sum / size;
    }
  }
  return out;
}

// Scale gas density, preserving every hole in the original cloud texture.
// Kept in sync with the GPU cloud-density formula for the Canvas fallback.
export function shapeCloudDensity(noise, density, formation) {
  const target = noise * (1 + Math.max(0, density) * 1.2);
  const amount = Math.max(0, Math.min(1, formation));
  return noise + (target - noise) * amount;
}

export function sampleFigureDensity(fields, weights, x, y) {
  if (!fields || x < 0 || y < 0 || x >= 1 || y >= 1) return 0;
  const index = Math.floor(y * FIELD_H) * FIELD_W + Math.floor(x * FIELD_W);
  let density = 0;
  for (let i = 0; i < fields.length; i++) density += (fields[i]?.density[index] || 0) * weights[i];
  return density;
}

function makeField(image) {
  const canvas = document.createElement('canvas');
  canvas.width = FIELD_W; canvas.height = FIELD_H;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;
  const scale = Math.min((FIELD_W - 24) / image.width, (FIELD_H - 24) / image.height);
  const width = image.width * scale; const height = image.height * scale;
  ctx.drawImage(image, (FIELD_W - width) / 2, (FIELD_H - height) / 2, width, height);
  const rgba = ctx.getImageData(0, 0, FIELD_W, FIELD_H).data;
  const alpha = Float32Array.from({ length: FIELD_W * FIELD_H }, (_, i) => rgba[i * 4 + 3] / 255);
  const medium = blurDensity(blurDensity(alpha, FIELD_W, FIELD_H, 4), FIELD_W, FIELD_H, 4);
  const broad = blurDensity(blurDensity(alpha, FIELD_W, FIELD_H, 13), FIELD_W, FIELD_H, 13);
  const pixels = ctx.createImageData(FIELD_W, FIELD_H);
  const density = new Float32Array(alpha.length);
  for (let i = 0; i < alpha.length; i++) {
    // Only diffuse volume survives. The original engraving lines are never
    // uploaded, avoiding an illuminated tracing even at full formation.
    const volume = broad[i] * 0.5 + medium[i] * 0.5;
    density[i] = volume / (volume + 0.08);
    pixels.data[i * 4] = pixels.data[i * 4 + 1] = pixels.data[i * 4 + 2] = density[i] * 255;
    pixels.data[i * 4 + 3] = 255;
  }
  ctx.putImageData(pixels, 0, 0);
  return { canvas, density, image };
}

export async function loadFigureFields(humours = false) {
  const fields = await Promise.all(FIGURE_KEYS.map((key) => new Promise((resolve) => {
    const sign = humours && key === 'alchemist' ? HUMOURS_SIGN : CLASS_SIGNS[key];
    const image = new Image();
    image.onload = () => {
      try {
        const field = makeField(sign === HUMOURS_SIGN ? engravingInk(image) : image);
        resolve(field ? { ...field, sign } : null);
      } catch { resolve(null); }
    };
    image.onerror = () => resolve(null);
    image.src = sign.image;
  })));
  const atlas = document.createElement('canvas');
  atlas.width = FIELD_W; atlas.height = FIELD_H * FIGURE_KEYS.length;
  const ctx = atlas.getContext('2d');
  if (!ctx) return null;
  ctx.fillStyle = '#000'; ctx.fillRect(0, 0, atlas.width, atlas.height);
  fields.forEach((field, i) => { if (field) ctx.drawImage(field.canvas, 0, i * FIELD_H); });
  return { atlas, fields };
}
